import React, { useEffect, useMemo, useRef, useCallback } from 'react';
import { Environment, OrbitControls, useGLTF } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import { EffectComposer, Bloom } from '@react-three/postprocessing';
import * as THREE from 'three';
import { trackSceneInteraction } from '../analytics';

const MODEL_PATH = '/3d/phone2.glb';
const HDR_PATH = '/3d/LeePerrySmith/photo_studio_loft_hall_256.hdr';
const DEFAULT_VIDEO_PATH = '/video/CFF.mp4';

const PHONE_POSITION = [-4, -3, -4];
const PHONE_ROTATION = [0, -1.2, 0.5];
const MIN_ZOOM_DISTANCE = 5;

const PhoneScene = ({ videoPath = DEFAULT_VIDEO_PATH, muted = true, volume = 1 }) => {
  const { scene } = useGLTF(MODEL_PATH);
  const { camera } = useThree();

  const screenVideo = useMemo(() => {
    const video = document.createElement('video');
    video.src = videoPath;
    video.crossOrigin = 'anonymous';
    video.loop = true;
    video.muted = true;
    video.playsInline = true;
    return video;
  }, [videoPath]);

  const screenTexture = useMemo(() => new THREE.VideoTexture(screenVideo), [screenVideo]);
  const hasTrackedInteraction = useRef(false);
  const defaultDistance = useRef(null);

  const trackFirstInteraction = useCallback(() => {
    if (!hasTrackedInteraction.current) {
      hasTrackedInteraction.current = true;
      trackSceneInteraction('PhoneScene', 'interact');
    }
  }, []);

  // Capture the default camera distance on mount so we can use it as the max zoom-out limit
  useEffect(() => {
    const target = new THREE.Vector3(...PHONE_POSITION);
    defaultDistance.current = camera.position.distanceTo(target);
  }, [camera]);

  // Video setup
  useEffect(() => {
    screenVideo.play().catch(() => undefined);
    screenTexture.flipY = false;
    screenTexture.colorSpace = THREE.SRGBColorSpace;
    screenTexture.needsUpdate = true;
  }, [screenVideo, screenTexture]);

  // Keep video mute state in sync with the audio toggle. If the browser
  // refuses unmuted playback (autoplay policy), fall back to muted so the
  // video keeps rendering.
  useEffect(() => {
    screenVideo.muted = muted;
    screenVideo.volume = Math.min(1, Math.max(0, volume));
    screenVideo.play().catch(() => {
      if (!screenVideo.muted) {
        screenVideo.muted = true;
        screenVideo.play().catch(() => undefined);
      }
    });
  }, [screenVideo, muted, volume]);

  // Apply video texture to screen mesh
  useEffect(() => {
    if (!scene) return;

    const screenMaterial = new THREE.MeshBasicMaterial({
      map: screenTexture,
      side: THREE.DoubleSide,
    });

    scene.traverse((child) => {
      if (child.isMesh && child.name === 'Phone_1') {
        child.material = screenMaterial;
      }
    });
  }, [scene, screenTexture]);

  return (
    <>
      <color attach="background" args={['rgb(14, 16, 17)']} />
      <Environment files={HDR_PATH} background={false} />

      <OrbitControls
        target={PHONE_POSITION}
        enablePan={false}
        enableDamping
        dampingFactor={0.1}
        minDistance={MIN_ZOOM_DISTANCE}
        maxDistance={defaultDistance.current ?? 12}
        onChange={trackFirstInteraction}
      />

      {/* Phone model */}
      <primitive
        object={scene}
        scale={1.6}
        position={PHONE_POSITION}
        rotation={PHONE_ROTATION}
      />

      <EffectComposer multisampling={0}>
        <Bloom intensity={2} luminanceThreshold={0.6} luminanceSmoothing={0.9} />
      </EffectComposer>
    </>
  );
};

useGLTF.preload(MODEL_PATH);

export default PhoneScene;
